import React from 'react';
import { guidesHubStructuredData } from '@/lib/docsLearnStructuredData';
import LearnHub from '@/components/Learn/LearnHub';
import LearnSurface from '@/components/Learn/LearnSurface';
import projects from '@/content/workProjects.json';
import { View } from 'react-native';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import { useLanding } from '@/components/Landing/LandingKit';
import { docsNavigation, getDocHref } from '@/constants/docsNavigation';
import useGuideNavigation from '@/hooks/useGuideNavigation';
import { DocsTopicLink } from './DocsDesign';
import DocsVisual from './DocsVisual';
import DocsVisualGallery from './DocsVisualGallery';
import { TutorialVideos } from '@/components/Tutorials/TutorialVideo';
import { getDocsNextStop } from '@/lib/tutorialVideos';

const START = [
  { href: '/docs/quickstart', title: 'Learn the essentials', description: 'Start your first conversation and get comfortable with the controls.' },
  { href: '/docs/agents', title: 'Make your own bot', description: 'Give a bot a role, customize its instructions, and save your favorites.' },
  { href: '/docs/api', title: 'Build with the API', description: 'Connect your application or coding agent with a Gab AI API key.' },
];
export default function DocsHome() {
  const { c, isCompact } = useLanding();
  const { sections } = useGuideNavigation('docs', { staticFallback: docsNavigation });
  return (
    <View>
      <MetaHead title="Gab AI Guides" description="Learn to use AI with free lessons, videos, and practice exercises. Find Gab AI documentation, feature guides, setup help, billing, and API reference." canonical="/docs" structuredData={guidesHubStructuredData()} />
      <View nativeID="learn"><LearnSurface><LearnHub /></LearnSurface></View>
      <Text nativeID="documentation" tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 32, marginTop: 56 }}>Documentation</Text>
      <View style={{ marginTop: 36 }}><TutorialVideos guideType="docs" slug="index" next={getDocsNextStop('index')} /></View>
      <View><DocsVisual visual="chat" priority tour /></View>
      <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 24, lineHeight: 31, letterSpacing: -0.5 }}>What would you like to do?</Text>
      <DocsVisualGallery />
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 0 : 36, marginTop: 28, marginBottom: 42 }}>
        {START.map(item => <View key={item.href} style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}><DocsTopicLink {...item} minHeight={112} /></View>)}
      </View>
      <View style={{ marginBottom: 36 }}>
        <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 24, lineHeight: 31, letterSpacing: -0.5, marginBottom: 12 }}>Popular guides</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 32 }}>
          {[['Start using Gab AI', '/docs/quickstart'], ['Write a good Work task', '/docs/work-prompts'], ['Set up the Windows app', '/docs/windows-app'], ['Set up the Mac app', '/docs/mac-app'], ['Download the apps', '/download'], ['Connect your phone with Remote', '/docs/remote'], ['Create documents, slides & sheets', '/docs/editors'], ['Generate AI videos', '/docs/videos'], ['Choose an AI model', '/docs/models'], ['Understand credits', '/docs/credits']].map(([title, href]) => <View key={href} style={{ width: isCompact ? '100%' : 'calc(50% - 16px)' }}><DocsTopicLink title={title} href={href} /></View>)}
        </View>
      </View>
      <View style={{ marginBottom: 32 }}>
        <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 24, lineHeight: 31, marginBottom: 12 }}>Make something with Work</Text>
        <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 25, marginBottom: 12 }}>Step-by-step briefs, artifact controls, and checks for the finished file.</Text>
        {projects.map(project => <DocsTopicLink key={project.slug} title={project.title} description={project.description} href={`/docs/${project.slug}`} />)}
      </View>
      {sections.map(section => (
        <View key={section.title} style={{ paddingTop: 28, paddingBottom: 20 }}>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 24, lineHeight: 31, letterSpacing: -0.5, marginBottom: 12 }}>{section.title}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 32 }}>
            {section.items.map(item => <View key={item.slug} style={{ width: isCompact ? '100%' : 'calc(50% - 16px)' }}><DocsTopicLink title={item.label} href={item.slug === 'index' ? '/docs/index' : item.href || getDocHref(item.slug)} /></View>)}
          </View>
        </View>
      ))}
    </View>
  );
}
